// transferService.ts - Version avec WebSocket et approbation backoffice
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// ================== CONFIGURATION AXIOS UNIFIÉE ==================

const createAxiosInstance = () => {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }
  });

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const axiosInstance = createAxiosInstance();

// ================== INTERFACES ==================

export interface BalanceAction {
  deposit: "deposit";
  withdrawal: "withdrawal";
}

export interface IntraMethodTransferRequest {
  method: string;
  currency: string;
  amount: number;
  from_action: "deposit" | "withdrawal";
  to_action: "deposit" | "withdrawal";
  note?: string;
}

export interface InterMethodTransferRequest {
  from_method: string;
  to_method: string;
  currency: string;
  amount: number;
  from_action: "deposit" | "withdrawal";
  to_action: "deposit" | "withdrawal";
  note?: string;
}

export interface CardToMobileMoneyTransferRequest {
  amount: number;
  currency: "USD" | "CDF";
  to_method: "mpesa" | "orange" | "airtel" | "afrimoney";
  to_action?: "deposit" | "withdrawal";
  note?: string;
}

// Interface pour un élément d'historique
export interface TransferHistoryItem {
  id: number;
  transfer_type: string;
  merchant_type?: number | null;
  source_method: string;
  target_method: string | null;
  amount: number;
  currency: string;
  created_at: string;
  reference: string | null;
  status: string | null;
  initiated_by: string | null;
  approved_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  comment: string | null;
  method: string | null;
  merchant_code: string | null;
  source_method: string | null;
  target_method: string | null;
  // Actual wallets debited (from balance_ledger). Differs from source_method
  // when biggest-first pulled from another operator's wallet.
  source_wallets?: string[];
  debit_plan?: Array<{ method: string; amount: number }>;
}

// Interface pour la réponse d'historique
export interface TransferHistoryResponse {
  success: boolean;
  message?: string;
  data: TransferHistoryItem[];
  total: number;
  currencyTotals: Record<string, number>;
  page: number;
  limit: number;
  totalPages: number;
}

// Interface pour les filtres
export interface TransferHistoryFilters {
  dateFrom?: string;
  dateTo?: string;
  operator?: string;
  currency?: string;
  status?: string;
  type?: string;
  method?: string;
  search?: string;
  reference?: string;
  merchant_code?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TransferResponse {
  merchant_code: string;
  transfer_id: string;
  reference: string;
  currency: string;
  amount: string;
  from_wallet: {
    method: string;
    action: string;
    after: any;
  };
  to_wallet: {
    method: string;
    action: string;
    after: any;
  };
  created_at: string;
  status: string;
  message?: string;
}

export interface BalanceCheckResponse {
  merchant_code: string;
  method: string;
  currency: string;
  realtime_deposit: string;
  realtime_withdrawal: string;
  reserved_deposit?: string;
  reserved_withdrawal: string;
  available?: string;
  in_flight?: string;
  total?: string;
  available_for_withdrawal?: string;
  action?: string;
  available_for_action?: string;
  last_transaction_id?: string;
  last_transaction_time?: string;
  updated_at?: string;
}

// Interface pour la compatibilité avec l'ancien code
export interface InitiateTransferRequest {
  merchant_code: string;
  method: string;
  currency: string;
  amount: number;
  comment?: string;
  initiated_by: string;
}

// Interfaces pour les transferts en attente (backoffice)
export interface PendingTransfer {
  reference: string;
  merchant_code: string;
  amount: number;
  currency: string;
  source_method: string;
  target_method: string;
  transfer_type: string;
  created_at: string;
  comment?: string;
  source_method?: string;
  target_method?: string;
}

export interface PendingTransfersResponse {
  success: boolean;
  data: PendingTransfer[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

export interface ApprovalRequest {
  reference: string;
  action: 'approve' | 'reject';
  comment?: string;
}

// ================== WEBSOCKET SERVICE ==================

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private merchantCode: string | null = null;
  private messageHandlers: Map<string, ((data: any) => void)[]> = new Map();
  private isIntentionalDisconnect = false;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  connect(merchantCode: string) {
    this.merchantCode = merchantCode;
    this.isIntentionalDisconnect = false;
    
    // 🔥 CORRECTION : Utiliser l'URL de l'API, pas l'URL du frontend
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    // Récupérer l'URL de base de l'API depuis les variables d'environnement
    const apiBaseUrl = import.meta.env.VITE_API_URL || "http://localhost:3944";
    
    // Extraire le hostname et le port de l'URL de l'API
    let wsHost: string;
    
    if (apiBaseUrl.startsWith('http://') || apiBaseUrl.startsWith('https://')) {
      // Si c'est une URL complète, extraire le hostname:port
      const url = new URL(apiBaseUrl);
      wsHost = url.host; // Cela donne "localhost:3944"
    } else {
      // Sinon, utiliser localhost:3944 par défaut
      wsHost = 'localhost:3944';
    }
    
    // 🔥 CORRECTION IMPORTANTE : Utiliser le même host/port que l'API
    const wsUrl = `${protocol}//${wsHost}/merchants/internal-transfers/ws/${merchantCode}`;
    
    console.log(`🔌 Connecting WebSocket for merchant ${merchantCode} to: ${wsUrl}`);
    
    // Ajouter le token d'authentification
    const token = localStorage.getItem("token");
    if (!token) {
      console.error('❌ No token found in localStorage');
      return;
    }

    // IR-2026-05-22: send the JWT via Sec-WebSocket-Protocol header instead of ?token=
    // so the token does NOT appear in nginx access logs.
    // Backend accepts both methods during the transition.
    this.socket = new WebSocket(wsUrl, [`bearer.${token}`]);

    this.socket.onopen = () => {
      console.log('✅ WebSocket connected successfully');
      this.reconnectAttempts = 0;
      this.notifyHandlers('connected', { merchantCode });
      // Démarrer le heartbeat
      this.startHeartbeat();
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 WebSocket message received:', data);
        this.notifyHandlers(data.type || 'unknown', data);
        
        if (data.type === 'transfer_status_update') {
          this.handleTransferStatusUpdate(data);
        }
      } catch (error) {
        console.error('❌ Error parsing WebSocket message:', error);
      }
    };

    this.socket.onclose = (event) => {
      console.log('🔌 WebSocket disconnected:', event.reason, event.code);
      // Arrêter le heartbeat
      this.stopHeartbeat();
      this.notifyHandlers('disconnected', { 
        reason: event.reason,
        code: event.code
      });

      // Ne pas essayer de se reconnecter si c'était une déconnexion intentionnelle
      if (this.isIntentionalDisconnect) {
        return;
      }
      
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => {
          this.reconnectAttempts++;
          console.log(`🔄 Attempting reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
          if (this.merchantCode) {
            this.connect(this.merchantCode);
          }
        }, this.reconnectDelay * this.reconnectAttempts);
      }
    };

    this.socket.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      console.error('❌ WebSocket readyState:', this.socket?.readyState);
      this.notifyHandlers('error', { error });
    };
  }
  disconnect() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

    private startHeartbeat() {
    // Envoyer un ping toutes les 30 secondes
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          this.socket.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
        } catch (error) {
          console.error('❌ Error sending heartbeat:', error);
          this.disconnect();
        }
      }
    }, 30000);
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  on(event: string, handler: (data: any) => void) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event)?.push(handler);
  }

  off(event: string, handler: (data: any) => void) {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private notifyHandlers(event: string, data: any) {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  private handleTransferStatusUpdate(data: any) {
    const notification = {
      title: 'Transfer Status Update',
      message: data.message,
      status: data.status,
      reference: data.reference,
      timestamp: data.timestamp
    };
    
    // Émettre un événement personnalisé
    window.dispatchEvent(new CustomEvent('transferNotification', { detail: notification }));
    
    // Afficher une notification toast (si disponible)
    this.showToastNotification(notification);
  }

  private showToastNotification(notification: any) {
    // Utilisez votre système de notification toast préféré ici
    console.log('📢 Transfer notification:', notification);
    
    // Exemple simple avec alert (à remplacer par votre système de toast)
    const message = `Transfer ${notification.reference}: ${notification.message}`;
    
    // Vous pouvez utiliser une bibliothèque comme react-hot-toast ou créer votre propre composant
    // Pour l'instant, on utilise console.log et on émet un événement
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          type: notification.status === 'SUCCESS' ? 'success' : 
                 notification.status === 'REJECTED' ? 'error' : 'info',
          message: message
        }
      }));
    }
  }
}

// Instance singleton WebSocket
export const websocketService = new WebSocketService();

export const connectWebSocket = (merchantCode: string) => {
  websocketService.connect(merchantCode);
  return websocketService;
};

export const disconnectWebSocket = () => {
  websocketService.disconnect();
};

// ================== FONCTIONS PRINCIPALES ==================

/**
 * Vérifier les soldes d'une méthode spécifique
 */
export const checkBalance = async (
  method: string,
  currency: string,
  action?: "deposit" | "withdrawal"
): Promise<BalanceCheckResponse> => {
  try {
    const params: any = { method, currency };
    if (action) params.action = action;

    const response = await axiosInstance.get(
      "/merchants/internal-transfers/balance-check",
      { params }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('❌ Error checking balance:', error);
    throw error;
  }
};

/**
 * Transfert intra-méthode (même opérateur)
 */
export const transferIntraMethod = async (
  payload: IntraMethodTransferRequest
): Promise<TransferResponse> => {
  try {
    const response = await axiosInstance.post(
      "/merchants/internal-transfers/intra-method",
      payload
    );
    
    return response.data;
  } catch (error: any) {
    console.error('❌ Error in intra-method transfer:', error);
    throw error;
  }
};

/**
 * Transfert inter-méthodes (différents opérateurs)
 */
export const transferInterMethod = async (
  payload: InterMethodTransferRequest
): Promise<TransferResponse> => {
  try {
    const response = await axiosInstance.post(
      "/merchants/internal-transfers/inter-method",
      payload
    );

    return response.data;
  } catch (error: any) {
    console.error('❌ Error in inter-method transfer:', error);
    throw error;
  }
};

/**
 * Transfert carte CyberSource -> wallet mobile money (bucket interne)
 * Débit cybersource_wallets.balance, crédit merchant_balance_realtime
 * selon {to_method, to_action, currency}.
 */
export const transferCardToMobileMoney = async (
  payload: CardToMobileMoneyTransferRequest
): Promise<TransferResponse> => {
  try {
    const response = await axiosInstance.post(
      "/merchants/internal-transfers/card-to-mobilemoney",
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ Error in card-to-mobilemoney transfer:', error);
    throw error;
  }
};

// ================== FONCTIONS POUR BACKOFFICE ==================

/**
 * Récupérer les transferts en attente d'approbation
 */
export const getPendingTransfersAdmin = async (
  page: number = 1,
  limit: number = 20,
  merchant_code?: string
): Promise<PendingTransfersResponse> => {
  try {
    const params: any = { page, limit };
    if (merchant_code) params.merchant_code = merchant_code;

    const response = await axiosInstance.get(
      "/merchants/internal-transfers/admin/pending-transfers",
      { params }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('❌ Error fetching pending transfers:', error);
    throw error;
  }
};

/**
 * Approuver ou rejeter un transfert
 */
export const approveTransfer = async (payload: ApprovalRequest): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      "/merchants/internal-transfers/admin/approve-transfer",
      payload
    );
    
    return response.data;
  } catch (error: any) {
    console.error('❌ Error approving transfer:', error);
    throw error;
  }
};

// ================== FONCTIONS DE COMPATIBILITÉ ==================

/**
 * Récupérer les transferts en attente (ancienne version - marchand)
 */
export const getPendingTransfers = async (): Promise<{
  success: boolean;
  message?: string;
  data: any[];
  total: number;
}> => {
  try {
    // Cette fonction est pour les marchands (non-admin)
    // Elle ne devrait renvoyer que leurs propres transferts en attente
    const response = await axiosInstance.get(
      "/merchants/internal-transfers/history",
      { 
        params: { 
          status: 'PENDING',
          page: 1,
          limit: 50
        } 
      }
    );
    
    // Filtrer pour ne garder que les transferts PENDING
    const pendingTransfers = response.data.data.filter(
      (transfer: TransferHistoryItem) => transfer.status === 'PENDING'
    );
    
    return {
      success: true,
      data: pendingTransfers,
      total: pendingTransfers.length
    };
  } catch (error: any) {
    console.error('❌ Error fetching pending transfers:', error);
    return {
      success: false,
      message: error.response?.data?.detail || error.message || 'Failed to fetch pending transfers',
      data: [],
      total: 0
    };
  }
};

/**
 * Compatibilité avec l'ancien code WalletToWallet
 */
export const initiateWalletToWallet = async (
  transferData: any
): Promise<any> => {
  try {
    const payload: IntraMethodTransferRequest = {
      method: transferData.method.toLowerCase() === 'mpesa' ? 'vodacom' : transferData.method,
      currency: transferData.currency,
      amount: transferData.amount,
      from_action: "deposit",
      to_action: "withdrawal",
      note: transferData.comment || 'Wallet to wallet transfer'
    };

    const response = await transferIntraMethod(payload);
    
    return {
      success: true,
      detail: `Transfer completed successfully! Reference: ${response.reference}`,
      data: response,
      reference: response.reference,
      status: response.status || 'COMPLETED'
    };
  } catch (error: any) {
    console.error('❌ Error in wallet to wallet transfer:', error);
    return {
      success: false,
      detail: error.response?.data?.detail || error.message || 'Failed to process transfer',
      status: 'FAILED'
    };
  }
};

/**
 * Compatibilité avec l'ancien code InterOperatorTransfer
 */
export const initiateInterOperatorTransfer = async (
  transferData: any
): Promise<any> => {
  try {
    const payload: InterMethodTransferRequest = {
      from_method: transferData.source_method.toLowerCase() === 'mpesa' ? 'vodacom' : transferData.source_method,
      to_method: transferData.target_method.toLowerCase() === 'mpesa' ? 'vodacom' : transferData.target_method,
      currency: transferData.currency,
      amount: transferData.amount,
      from_action: transferData.source_wallet_type,
      to_action: transferData.target_wallet_type,
      note: transferData.comment || 'Inter-operator transfer'
    };

    const response = await transferInterMethod(payload);
    
    return {
      success: true,
      detail: `Inter-operator transfer completed successfully! Reference: ${response.reference}`,
      data: response,
      reference: response.reference,
      status: response.status || 'COMPLETED',
      transfer_type: `inter_${payload.from_method}_to_${payload.to_method}`,
      auto_validated: response.status === 'SUCCESS',
      validation_data: {
        reference: response.reference,
        processed_by: response.status === 'SUCCESS' ? 'System' : 'Pending',
        processed_at: new Date().toISOString(),
        status: response.status || 'PENDING'
      }
    };
  } catch (error: any) {
    console.error('❌ Error in inter-operator transfer:', error);
    return {
      success: false,
      detail: error.response?.data?.detail || error.message || 'Failed to process transfer',
      status: 'FAILED'
    };
  }
};

/**
 * Fonction de compatibilité pour getMerchantBalances
 */
export const getMerchantBalances = async (
  merchantCode: string,
  currency: string,
  method: string
): Promise<{
  deposit_balance: number;
  withdrawal_balance: number;
  reserved_deposit: number;
  reserved_withdrawal: number;
  available: number;
  in_flight: number;
  total_balance: number;
  currency: string;
  method: string;
  last_updated: string;
}> => {
  try {
    const balanceResponse = await checkBalance(method, currency);

    const rd = parseFloat(balanceResponse.realtime_deposit) || 0;
    const rw = parseFloat(balanceResponse.realtime_withdrawal) || 0;
    const resD = parseFloat(balanceResponse.reserved_deposit ?? "0") || 0;
    const resW = parseFloat(balanceResponse.reserved_withdrawal ?? "0") || 0;

    const available = balanceResponse.available !== undefined
      ? parseFloat(balanceResponse.available)
      : (rd - resD) + (rw - resW);
    const inFlight = balanceResponse.in_flight !== undefined
      ? parseFloat(balanceResponse.in_flight)
      : resD + resW;
    const total = balanceResponse.total !== undefined
      ? parseFloat(balanceResponse.total)
      : rd + rw;

    return {
      deposit_balance: rd,
      withdrawal_balance: rw,
      reserved_deposit: resD,
      reserved_withdrawal: resW,
      available,
      in_flight: inFlight,
      total_balance: total,
      currency,
      method,
      last_updated: balanceResponse.updated_at || new Date().toISOString()
    };
  } catch (error: any) {
    console.error('❌ Error fetching merchant balances:', error);
    throw error;
  }
};

/**
 * Fonction simplifiée - plus de maker/checker nécessaire
 */
export const requiresMakerChecker = async (merchantCode: string): Promise<boolean> => {
  // Avec les nouvelles API, les transferts peuvent être PENDING
  return true; // Modifier selon votre logique métier
};

// ================== FONCTIONS D'HISTORIQUE ==================

/**
 * Récupérer l'historique des transferts
 */
export const getTransferHistory = async (
  filters: TransferHistoryFilters
): Promise<TransferHistoryResponse> => {
  try {
    const params: any = {
      page: filters.page || 1,
      limit: filters.limit || 10,
    };

    // Ajouter les filtres optionnels
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.operator && filters.operator !== 'all') params.operator = filters.operator;
    if (filters.currency && filters.currency !== 'all') params.currency = filters.currency;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.type && filters.type !== 'all') params.type = filters.type;
    if (filters.method && filters.method !== 'all') params.method = filters.method;
    if (filters.search) params.search = filters.search;
    if (filters.reference) params.reference = filters.reference;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;

    const response = await axiosInstance.get(
      "/merchants/internal-transfers/history",
      { params }
    );
    
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error fetching transfer history:', error);
    return {
      success: false,
      message: error.response?.data?.detail || error.message || 'Failed to fetch transfer history',
      data: [],
      total: 0,
      currencyTotals: {},
      page: filters.page || 1,
      limit: filters.limit || 10,
      totalPages: 0
    };
  }
};

/**
 * Récupérer les détails d'un transfert
 */
export const getTransferDetails = async (reference: string): Promise<TransferHistoryItem> => {
  try {
    const response = await axiosInstance.get(
      `/merchants/internal-transfers/history/${reference}`
    );
    
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error fetching transfer details:', error);
    throw error;
  }
};


/**
 * Exporter l'historique des transferts
 */
export const exportTransferHistory = async (
  filters: TransferHistoryFilters
): Promise<Blob> => {
  try {
    const params: any = {};

    // Ajouter les filtres optionnels
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.operator && filters.operator !== 'all') params.operator = filters.operator;
    if (filters.currency && filters.currency !== 'all') params.currency = filters.currency;
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.type && filters.type !== 'all') params.type = filters.type;
    if (filters.method && filters.method !== 'all') params.method = filters.method;
    if (filters.search) params.search = filters.search;
    if (filters.reference) params.reference = filters.reference;

    const response = await axiosInstance.get(
      "/merchants/internal-transfers/history/export/csv",
      {
        params,
        responseType: 'blob'  // Important pour télécharger un fichier
      }
    );
    
    return response.data;
    
  } catch (error: any) {
    console.error('❌ Error exporting transfer history:', error);
    throw error;
  }
};

// ================== FONCTIONS UTILITAIRES ==================

/**
 * Obtenir les opérateurs disponibles
 */
export const getAvailableOperators = (): Array<{
  id: string;
  name: string;
  color: string;
}> => {
  const operatorColors: { [key: string]: string } = {
    'vodacom': '#E60000',
    'orange': '#FF8C00',
    'airtel': '#E53E3E',
    'afrimoney': '#6B46C1',
  };

  return [
    { id: 'vodacom', name: 'M-Pesa', color: operatorColors['vodacom'] },
    { id: 'orange', name: 'Orange', color: operatorColors['orange'] },
    { id: 'airtel', name: 'Airtel', color: operatorColors['airtel'] },
    { id: 'afrimoney', name: 'Afrimoney', color: operatorColors['afrimoney'] }
  ];
};

/**
 * Formater le montant pour l'affichage
 */
export const formatAmount = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount) + ` ${currency}`;
};

/**
 * Fonction de statistiques (à implémenter si nécessaire)
 */
export const getTransferStatistics = async () => {
  try {
    const response = await getTransferHistory({
      page: 1,
      limit: 100,
      sortBy: 'created_at',
      sortOrder: 'desc'
    });

    const stats = {
      totalTransfers: response.total,
      totalAmount: Object.values(response.currencyTotals).reduce((a, b) => a + b, 0),
      successCount: response.data.filter(t => t.status === 'SUCCESS').length,
      pendingCount: response.data.filter(t => t.status === 'PENDING').length,
      failedCount: response.data.filter(t => t.status === 'FAILED' || t.status === 'REJECTED').length,
      byCurrency: response.currencyTotals,
      byType: {},
      byMethod: {},
      byStatus: {},
      recentTransfers: response.data.slice(0, 5)
    };

    // Calculer les statistiques par type
    response.data.forEach(transfer => {
      // Par type
      const type = transfer.transfer_type;
      // @ts-ignore
      stats.byType[type] = (stats.byType[type] || 0) + 1;
      
      // Par méthode
      const method = transfer.method || 'unknown';
      // @ts-ignore
      stats.byMethod[method] = (stats.byMethod[method] || 0) + 1;
      
      // Par statut
      const status = transfer.status || 'unknown';
      // @ts-ignore
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error('Error getting transfer statistics:', error);
    return {
      totalTransfers: 0,
      totalAmount: 0,
      successCount: 0,
      pendingCount: 0,
      failedCount: 0,
      byCurrency: {},
      byType: {},
      byMethod: {},
      byStatus: {},
      recentTransfers: []
    };
  }
};

// ================== EXPORT ==================

const transferService = {
  // Nouvelles fonctions principales
  checkBalance,
  transferIntraMethod,
  transferInterMethod,
  
  // Fonctions backoffice
  getPendingTransfersAdmin,
  approveTransfer,
  
  // Fonctions de compatibilité
  initiateWalletToWallet,
  initiateInterOperatorTransfer,
  getMerchantBalances,
  requiresMakerChecker,
  
  // Fonctions utilitaires
  getAvailableOperators,
  formatAmount,
  getPendingTransfers,
  
  // WebSocket
  connectWebSocket,
  disconnectWebSocket,
  websocketService,
  
  // Fonctions d'historique
  getTransferHistory,
  getTransferDetails,
  exportTransferHistory,
  getTransferStatistics,
};

export default transferService;